import type { Order } from '@/types/supervisor';
import { selectOrderState, type OrderItem } from '@/features/order/orderSlice';

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isPositiveNumber = (value: unknown): value is number => isFiniteNumber(value) && value > 0;

const isValidPricingStatus = (value: unknown): value is 'idle' | 'loading' | 'succeeded' | 'failed' =>
  value === 'idle' || value === 'loading' || value === 'succeeded' || value === 'failed';

const normalizePrintQualityWarning = (value: unknown): Order['items'][number]['printQualityWarning'] | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const candidate = value as Partial<NonNullable<Order['items'][number]['printQualityWarning']>>;
  if (candidate.type !== 'LOW_PPI') {
    return undefined;
  }
  if (!isFiniteNumber(candidate.ppi) || candidate.ppi <= 0 || !isFiniteNumber(candidate.threshold) || candidate.threshold <= 0) {
    return undefined;
  }
  return {
    type: 'LOW_PPI',
    ppi: candidate.ppi,
    threshold: candidate.threshold,
    ...(typeof candidate.acknowledged === 'boolean' ? { acknowledged: candidate.acknowledged } : {}),
    ...(isNonEmptyString(candidate.reason) ? { reason: candidate.reason.trim() } : {}),
  };
};

type ReduxOrderState = ReturnType<typeof selectOrderState>;

type SupervisorOrder = (Order & Record<string, unknown>) | null | undefined;

export const sanitizeOrderItems = (items: ReadonlyArray<any>): Order['items'] => {
  return items
    .filter((candidate): candidate is Partial<Order['items'][number]> & { id: string } => {
      if (!candidate) {
        return false;
      }
      const id = (candidate as { id?: unknown }).id;
      return isNonEmptyString(id);
    })
    .map((item) => {
      const normalized: Order['items'][number] = { id: (item.id as string).trim() };

      if (isNonEmptyString(item.uploadedFileId)) {
        normalized.uploadedFileId = item.uploadedFileId.trim();
      }
      if (isNonEmptyString(item.paperTypeId)) {
        normalized.paperTypeId = item.paperTypeId.trim();
      }
      if (isNonEmptyString(item.printerId)) {
        normalized.printerId = item.printerId.trim();
      }
      if (isNonEmptyString(item.orderId)) {
        normalized.orderId = item.orderId.trim();
      }
      if (isFiniteNumber(item.quantity) && item.quantity > 0) {
        normalized.quantity = Math.min(99, Math.max(1, Math.round(item.quantity)));
      }

      if (isPositiveNumber(item.width)) {
        normalized.width = item.width;
      }
      if (isPositiveNumber(item.height)) {
        normalized.height = item.height;
      }
      if (isPositiveNumber(item.widthIn)) {
        normalized.widthIn = item.widthIn;
      }
      if (isPositiveNumber(item.heightIn)) {
        normalized.heightIn = item.heightIn;
      }
      if (isFiniteNumber(item.marginIn)) {
        normalized.marginIn = item.marginIn;
      }
      if (isFiniteNumber(item.galleryWrapIn)) {
        normalized.galleryWrapIn = item.galleryWrapIn;
      }
      if (typeof item.galleryWrapMirrorEdges === 'boolean') {
        normalized.galleryWrapMirrorEdges = item.galleryWrapMirrorEdges;
      }
      if (isPositiveNumber(item.originalWidthPx)) {
        normalized.originalWidthPx = item.originalWidthPx;
      }
      if (isPositiveNumber(item.originalHeightPx)) {
        normalized.originalHeightPx = item.originalHeightPx;
      }
      if (isPositiveNumber(item.originalWidthIn)) {
        normalized.originalWidthIn = item.originalWidthIn;
      }
      if (isPositiveNumber(item.originalHeightIn)) {
        normalized.originalHeightIn = item.originalHeightIn;
      }
      if (isPositiveNumber(item.dpiX)) {
        normalized.dpiX = item.dpiX;
      }
      if (isPositiveNumber(item.dpiY)) {
        normalized.dpiY = item.dpiY;
      }
      const printQualityWarning = normalizePrintQualityWarning(item.printQualityWarning);
      if (printQualityWarning) {
        normalized.printQualityWarning = printQualityWarning;
      } else if (item.printQualityWarning === null) {
        normalized.printQualityWarning = null;
      }

      const statusCandidate = typeof item.status === 'string' ? item.status.trim().toUpperCase() : undefined;
      if (
        statusCandidate === 'IN_ORDER' ||
        statusCandidate === 'REUPLOAD_IMAGE' ||
        statusCandidate === 'REUPLOADED' ||
        statusCandidate === 'READY_TO_PRINT' ||
        statusCandidate === 'PRINTED'
      ) {
        normalized.status = statusCandidate;
      } else if (statusCandidate === 'NEW' || statusCandidate === 'CONFIGURED' || statusCandidate === 'CONFIGURING') {
        normalized.status = 'NEW';
      }

      const inferredOrderId = (item as { orderId?: unknown }).orderId;
      if (
        isNonEmptyString(inferredOrderId)
        && (normalized.status === undefined || normalized.status === 'NEW')
      ) {
        normalized.status = 'IN_ORDER';
      }

      if (item.price && typeof item.price === 'object' && item.price !== null) {
        const value = (item.price as { value?: unknown }).value;
        const currency = (item.price as { currency?: unknown }).currency;
        if (isFiniteNumber(value) && isNonEmptyString(currency)) {
          normalized.price = { value, currency: currency.trim() };
        }
      }

      if (isValidPricingStatus(item.pricingStatus)) {
        normalized.pricingStatus = item.pricingStatus;
      }

      if (isNonEmptyString(item.pricingError)) {
        normalized.pricingError = item.pricingError.trim();
      }

      const colorMode = item.colorMode;
      if (colorMode === 'COLOR' || colorMode === 'BLACK_WHITE') {
        normalized.colorMode = colorMode;
      }

      if (isNonEmptyString(item.createdAt)) {
        normalized.createdAt = item.createdAt.trim();
      }
      if (isNonEmptyString(item.updatedAt)) {
        normalized.updatedAt = item.updatedAt.trim();
      }

      return normalized;
    });
};

export const buildCanonicalOrder = (
  reduxOrder: ReduxOrderState | undefined,
  supervisorOrder?: SupervisorOrder,
): (Order & Record<string, unknown>) => {
  const fallbackOrder: SupervisorOrder = supervisorOrder ?? { items: [] };
  const supervisorItems = Array.isArray(supervisorOrder?.items) ? supervisorOrder.items : [];
  const supervisorById = new Map<string, any>();

  for (const item of supervisorItems) {
    if (item && typeof item.id === 'string' && item.id.trim().length > 0) {
      supervisorById.set(item.id, item);
    }
  }

  const candidates: any[] = [];
  const reduxItems: OrderItem[] = Array.isArray(reduxOrder?.items) ? (reduxOrder?.items as OrderItem[]) : [];

  for (const item of reduxItems) {
    if (!item || typeof item.id !== 'string' || item.id.trim().length === 0) {
      continue;
    }
    const fallback = supervisorById.get(item.id);
    candidates.push({
      ...(fallback ?? {}),
      ...item,
      id: item.id,
      orderId: item.orderId ?? fallback?.orderId ?? null,
      uploadedFileId: item.uploadedFileId ?? fallback?.uploadedFileId,
      quantity: item.quantity ?? fallback?.quantity,
      paperTypeId: item.paperTypeId ?? fallback?.paperTypeId,
      printerId: item.printerId ?? fallback?.printerId,
      widthIn: item.widthIn ?? fallback?.widthIn,
      heightIn: item.heightIn ?? fallback?.heightIn,
      marginIn: item.marginIn ?? fallback?.marginIn,
      galleryWrapIn: item.galleryWrapIn ?? fallback?.galleryWrapIn,
      galleryWrapMirrorEdges: item.galleryWrapMirrorEdges ?? fallback?.galleryWrapMirrorEdges,
      price: item.price ?? fallback?.price,
      pricingStatus: item.pricingStatus ?? fallback?.pricingStatus,
      pricingError: item.pricingError ?? fallback?.pricingError,
      colorMode: item.colorMode ?? fallback?.colorMode,
      originalWidthPx: item.originalWidthPx ?? fallback?.originalWidthPx,
      originalHeightPx: item.originalHeightPx ?? fallback?.originalHeightPx,
      originalWidthIn: item.originalWidthIn ?? fallback?.originalWidthIn,
      originalHeightIn: item.originalHeightIn ?? fallback?.originalHeightIn,
      dpiX: item.dpiX ?? fallback?.dpiX,
      dpiY: item.dpiY ?? fallback?.dpiY,
      printQualityWarning: item.printQualityWarning ?? fallback?.printQualityWarning,
      createdAt: item.createdAt ?? fallback?.createdAt,
      updatedAt: item.updatedAt ?? fallback?.updatedAt,
    });
    supervisorById.delete(item.id);
  }

  for (const fallback of supervisorById.values()) {
    candidates.push(fallback);
  }

  const items = sanitizeOrderItems(candidates);

  const mergedOrder: Order & Record<string, unknown> = {
    ...(fallbackOrder ?? {}),
    items,
  } as Order & Record<string, unknown>;

  if (reduxOrder?.shippingAddress) {
    mergedOrder.shippingAddress = reduxOrder.shippingAddress;
  }

  if (reduxOrder?.id) {
    mergedOrder.id = reduxOrder.id;
  }

  if (reduxOrder?.status) {
    mergedOrder.status = reduxOrder.status;
  }

  if (reduxOrder?.pricingSummary) {
    mergedOrder.pricingSummary = reduxOrder.pricingSummary;
  }

  if (reduxOrder?.paymentInformation) {
    mergedOrder.paymentInformation = reduxOrder.paymentInformation;
  }

  return mergedOrder;
};
